import { useEffect, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { useMe } from "@/hooks/useAuth"
import type { CategoryEntry } from "@/types/api"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

type Preferences = {
  interest_tags: string[]
  summary_length: "1line" | "3line" | "long"
  difficulty: "intro" | "practical" | "research"
  languages: string[]
  newsletter_enabled: boolean
  newsletter_frequency: "daily" | "weekly"
  newsletter_send_at: string  // "HH:MM:SS"
  newsletter_timezone: string
  prefer_with_code: boolean
}

export default function PreferencesPage() {
  const { data: me } = useMe()
  const qc = useQueryClient()
  const { data: prefs } = useQuery<Preferences>({
    queryKey: ["me-preferences"],
    queryFn: () => api.get<Preferences>("/me/preferences"),
  })
  const { data: categories } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<CategoryEntry[]>("/categories"),
    staleTime: 5 * 60_000,
  })

  const [draft, setDraft] = useState<Preferences | null>(null)
  useEffect(() => { if (prefs && !draft) setDraft(prefs) }, [prefs, draft])

  const save = useMutation({
    mutationFn: (changes: Partial<Preferences>) =>
      api.put<Preferences>("/me/preferences", changes),
    onSuccess: (data) => {
      qc.setQueryData(["me-preferences"], data)
      setDraft(data)
    },
  })

  if (!draft) return <div className="text-sm text-muted-foreground">불러오는 중...</div>

  const toggleTag = (tag: string) => {
    const has = draft.interest_tags.includes(tag)
    setDraft({
      ...draft,
      interest_tags: has
        ? draft.interest_tags.filter((t) => t !== tag)
        : [...draft.interest_tags, tag],
    })
  }

  const changed = JSON.stringify(draft) !== JSON.stringify(prefs)

  const submit = () => {
    if (!prefs) return
    const changes: Partial<Preferences> = {}
    for (const k of Object.keys(draft) as (keyof Preferences)[]) {
      if (JSON.stringify(draft[k]) !== JSON.stringify(prefs[k])) {
        (changes as Record<string, unknown>)[k] = draft[k]
      }
    }
    save.mutate(changes)
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">설정</h1>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>계정</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
            <dt className="text-muted-foreground">이메일</dt>
            <dd>{me?.email}</dd>
            <dt className="text-muted-foreground">이름</dt>
            <dd>{me?.display_name || "-"}</dd>
            <dt className="text-muted-foreground">권한</dt>
            <dd>{me?.role}</dd>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>관심 카테고리</CardTitle>
          <CardDescription>선택한 태그가 점수 개인화에 반영됩니다.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {categories?.map((c) => (
              <button
                key={c.tag}
                onClick={() => toggleTag(c.tag)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm transition-colors",
                  draft.interest_tags.includes(c.tag)
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:opacity-80",
                )}
                title={c.label_ko}
              >
                {c.tag}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>요약 / 난이도</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">요약 길이</Label>
            <div className="mt-2 flex gap-2">
              {(["1line", "3line", "long"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setDraft({ ...draft, summary_length: v })}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm",
                    draft.summary_length === v
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground",
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">난이도</Label>
            <div className="mt-2 flex gap-2">
              {(["intro", "practical", "research"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setDraft({ ...draft, difficulty: v })}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm",
                    draft.difficulty === v
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground",
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.prefer_with_code}
              onChange={(e) => setDraft({ ...draft, prefer_with_code: e.target.checked })}
            />
            코드 구현 있는 논문 우선
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>뉴스레터 수신</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.newsletter_enabled}
              onChange={(e) => setDraft({ ...draft, newsletter_enabled: e.target.checked })}
            />
            뉴스레터 받기
          </label>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">빈도</Label>
            <div className="mt-2 flex gap-2">
              {(["daily", "weekly"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setDraft({ ...draft, newsletter_frequency: v })}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm",
                    draft.newsletter_frequency === v
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground",
                  )}
                  disabled={!draft.newsletter_enabled}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="send_at" className="text-xs uppercase text-muted-foreground">
                발송 시각
              </Label>
              <Input
                id="send_at"
                type="time"
                value={draft.newsletter_send_at.slice(0, 5)}
                onChange={(e) =>
                  setDraft({ ...draft, newsletter_send_at: `${e.target.value}:00` })
                }
                disabled={!draft.newsletter_enabled}
              />
            </div>
            <div>
              <Label htmlFor="tz" className="text-xs uppercase text-muted-foreground">
                시간대
              </Label>
              <Input
                id="tz"
                type="text"
                value={draft.newsletter_timezone}
                onChange={(e) => setDraft({ ...draft, newsletter_timezone: e.target.value })}
                disabled={!draft.newsletter_enabled}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 pt-2">
        <Button onClick={submit} disabled={!changed || save.isPending}>
          {save.isPending ? "저장 중..." : "변경 저장"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => setDraft(prefs!)}
          disabled={!changed || save.isPending}
        >
          되돌리기
        </Button>
        {save.isError && (
          <span className="text-sm text-destructive">{(save.error as Error).message}</span>
        )}
        {save.isSuccess && !changed && (
          <span className="text-sm text-muted-foreground">저장됨</span>
        )}
      </div>
    </div>
  )
}
